// Import Packages
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';

// Import Material UI
import { Dialog, Button, makeStyles, TextField } from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

// Import Recoil
import { alertState } from '../../../../../recoil-store/recoil-state';

// Import Pages
import FieldLabel from '../../../../FlowConfigurationInputs/FieldLabel/FieldLabel';

// Import Icons
import CloseIcon from '../../../../../assets/icons/close-icon.svg';


const useStyles = makeStyles(theme => ({
    dialogContainer: {
        display: 'flex',
        justifyContent: 'space-between',
        background: '#FFFFFF'
    },
    inputRoot: {
        "& fieldset": {
            borderColor: '#E6E6E6',
            borderWidth: '1.1px'
        }
    }
}));

function TransformList({ previewData, outputVariableName, transformKeys, cancel, save }) {
    const [selectedArea, setSelectedArea] = useState({ keys: [], type: '', active: false });
    const [manuelRowNumber, setManuelRowNumber] = useState();
    const [manuelColNumber, setManuelColNumber] = useState();
    const [transformValueType, setTransformValueType] = useState();
    const [showManuelTransform, setShowManuelTransform] = useState(false);
    const [keys, setKeys] = useState([]);

    const setAlert = useSetRecoilState(alertState);
    const classes = useStyles();
    const { t } = useTranslation();
    const label = "CONTENT.CONFIGURATION-FORM.ADVANCED-ACTIONS.DATA-TRANSFORM.";

    useEffect(() => {
        setSelectedArea({ keys: [], type: '', active: false });
    }, [previewData, transformKeys])

    const showSelectedTransform = () => {
        let c = transformKeys && transformKeys.length > 0 ? <span className='select-area-range-value'>{" ; "}</span> : "";

        if (selectedArea?.active) {
            let path = `${outputVariableName}`;
            for (const key of selectedArea.keys) {
                path += `[${key}]`;
            }

            if (!transformKeys.find(x => x === path)) {
                return <span className='select-area-range-value'>
                    {c}
                    <span className='range-bold icon-cursor-pointer f-s-12'>({path})</span>
                </span>
            }
        } else if (!c) {
            return (
                <span className='select-area-no-content'>{t(label + "EXISTING-TRANSFORM")}</span>
            )
        }
    }


    const getTransform = (path, i) => {
        const pattern = /\[([^\]]+)\]/g;
        let keyList = [];
        let match;

        while ((match = pattern.exec(path)) !== null) {
            keyList.push(match[1]);
        }

        let cls = "icon-cursor-pointer";
        if (selectedArea?.active && JSON.stringify(keyList) === JSON.stringify(selectedArea.keys)) {
            cls = "icon-cursor-pointer range-bold"
        }
        let seperator = "";
        if (i !== 0) {
            seperator = " ; ";
        }
        return <span className="select-area-range-value f-s-12">
            {seperator}
            <span onClick={() => setSelectedArea({ keys: keyList, type: '', active: true })} className={cls}>
                ({path})
            </span>
        </span>
    }

    const selectManuelTransform = () => {
        if (previewData.type === "data table") {
            if (!manuelRowNumber || !manuelColNumber) {
                return setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t('ACTIVE-DIRECTORY.NOT-NULL'),
                    severity: 'error'
                }))
            }
            setSelectedArea({ keys: [manuelRowNumber - 1, manuelColNumber - 1], type: 'string', active: true });
        } else {
            if (keys.length) {
                if (!transformValueType) {
                    return setAlert(alert => ({
                        ...alert,
                        show: true,
                        message: t('ACTIVE-DIRECTORY.NOT-NULL'),
                        severity: 'error'
                    }))
                }

                setSelectedArea({ keys: [...keys], type: transformValueType, active: true });
            } else {
                setSelectedArea({ keys: [], type: previewData.type, active: true });
            }
        }

        setManuelColNumber(undefined);
        setManuelRowNumber(undefined);
        setTransformValueType("");
        setKeys([]);
        setShowManuelTransform(false);
    }

    const handleChange = (e, newValue) => {
        if (e.key && e.key === "Enter") {
            if (!e.target.value) {
                return;
            } else {
                setKeys([...keys, e.target.value]);
            }
        } else {
            setKeys(newValue);
        }
    }

    const handleChangeType = (e, newValue) => {
        setTransformValueType(newValue);
    }

    const manuelRange = () => {

        const showPreviewKeys = () => {
            let v = `##${outputVariableName}`;
            for (const key of keys) {
                v += `[${key}]`;
            }
            v += "##";
            return (
                <div className='variable-preview-container'>
                    {v}
                </div>
            )
        }

        return showManuelTransform &&
            <Dialog open={true} className='base-dialog-content' PaperProps={{ className: classes.dialogContainer }}>
                <div className='manuel-range-modal-container min-w-350'>
                    <div className='items-center-between'>
                        <div className='base-modal-title base-title-detail'>
                            {t(label + "ADDING-MANUEL-TRANSFORM")}
                        </div>
                        <img className='transform-cancel-icon' src={CloseIcon} onClick={() => setShowManuelTransform(false)} />
                    </div>
                    {
                        previewData.type === "data table" ?
                            <div>
                                <div className='m-t-20'>
                                    <TextField
                                        fullWidth={true}
                                        type='number'
                                        value={manuelRowNumber}
                                        className="select-action-input"
                                        onChange={(v) => {
                                            if (parseInt(v.target.value) > 0) {
                                                setManuelRowNumber(v.target.value)
                                            }
                                        }}
                                        placeholder={t(label + "ROW-NUMBER")}
                                        variant="outlined"
                                        classes={{
                                            root: classes.inputRoot
                                        }}
                                        inputProps={{
                                            style: {
                                                marginLeft: '5px',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#46474E'
                                            }
                                        }}
                                    />
                                </div>
                                <div className='m-t-10'>
                                    <TextField
                                        fullWidth={true}
                                        type='number'
                                        value={manuelColNumber}
                                        onChange={(v) => {
                                            if (parseInt(v.target.value) > 0) {
                                                setManuelColNumber(v.target.value)
                                            }
                                        }}
                                        className="select-action-input"
                                        placeholder={t(label + "COL-NUMBER")}
                                        variant="outlined"
                                        classes={{
                                            root: classes.inputRoot
                                        }}
                                        inputProps={{
                                            style: {
                                                marginLeft: '5px',
                                                fontSize: 12,
                                                fontWeight: 600,
                                                color: '#46474E'
                                            }
                                        }}
                                    />
                                </div>

                            </div> :
                            <div>
                                {showPreviewKeys()}
                                <div className="conf-field-container">
                                    <FieldLabel name={t(label + 'VARIABLE-KEYS')} />
                                    <Autocomplete
                                        id='variable-key-list-dropdown'
                                        classes={{
                                            option: classes.option,
                                            inputRoot: classes.inputRoot,
                                            popupIndicator: classes.popupIndicator
                                        }}
                                        style={{ position: 'relative', background: '#FBFBFB' }}
                                        freeSolo
                                        multiple
                                        onChange={handleChange}
                                        value={keys}
                                        size="small"
                                        options={[]}
                                        disableCloseOnSelect
                                        getOptionLabel={(option) => option}
                                        renderInput={params => <TextField {...params} variant="outlined" />}
                                    />
                                    <small className='conf-field-desc'><em>{t(label + "VARIABLE-KEYS-INFO")}</em></small>
                                </div>

                                {
                                    !keys.length ? undefined :
                                        <div className="conf-field-container m-t-20">
                                            <FieldLabel name={t(label + 'VALUE-TYPE')} is_required />
                                            <Autocomplete
                                                id='value-type-list-dropdown'
                                                classes={{
                                                    option: classes.option,
                                                    inputRoot: classes.inputRoot,
                                                    popupIndicator: classes.popupIndicator
                                                }}
                                                style={{ width: "90%", position: 'relative', background: '#FBFBFB' }}
                                                onChange={handleChangeType}
                                                value={transformValueType}
                                                size="small"
                                                options={["string", "number", "boolean", "date", "object", "array", "data table"]}
                                                getOptionLabel={(option) => option}
                                                renderInput={params => <TextField {...params} variant="outlined" />}
                                            />
                                        </div>
                                }
                            </div>
                    }
                    <div className='configuration-footer'>
                        <Button className='transform-submit-button m-w-140' onClick={selectManuelTransform}>
                            {t(label + 'SELECT')}
                        </Button>
                    </div>
                </div>

            </Dialog >
    }

    const selectValue = () => {
        let path;
        let type;
        if (!selectedArea.active) {
            return setAlert(alert => ({
                ...alert,
                show: true,
                message: t(label + 'PATH-NOT-SELECTED'),
                severity: 'error'
            }))
        }

        if (!selectedArea.keys.length) {
            type = previewData.type;
        } else if (previewData.type === "data table") {
            if (selectedArea.keys.length !== 2) {
                return setAlert(alert => ({
                    ...alert,
                    show: true,
                    message: t(label + 'DATA-TABLE-RANGE-ERROR'),
                    severity: 'error'
                }))
            }

            type = "string";
        } else {
            if (selectedArea.type) {
                type = selectedArea.type;
            }
        }

        path = `${outputVariableName}`;
        for (const key of selectedArea.keys) {
            path += `[${key}]`;
        }

        save(path, type);
    }

    return (
        <Dialog open={true} className='base-dialog-content m-l-y-45' maxWidth={850} PaperProps={{ className: classes.dialogContainer }}>
            <div className='data-table-select-container transform-list-container'>

                <div className='items-center-between'>
                    <div className='base-modal-title base-title-detail'>
                        {t(label + "TRANSFORM-LIST")}
                    </div>
                    <img className='transform-cancel-icon' src={CloseIcon} onClick={cancel} />
                </div>
                <div className='transform-sub-description'>
                    {t(label + "TRANSFORM-LIST-DESC")}
                </div>

                <div className='data-table-select-area transform-history-container'>
                    {transformKeys &&
                        transformKeys.map((path, i) =>
                            getTransform(path, i)
                        )
                    }

                    {showSelectedTransform()}

                </div>
                <div className='manuel-range-container'>
                    <Button className='manuel-range-button' onClick={() => setShowManuelTransform(true)}>
                        {t(label + 'ADDING-MANUEL-TRANSFORM')}
                    </Button>
                </div>

                <div className='configuration-footer'>
                    <Button className='transform-submit-button m-w-140' onClick={selectValue}>
                        {t(label + 'SELECT-VALUE')}
                    </Button>
                </div>

                {manuelRange()}

            </div >
        </Dialog >
    )
}

export default TransformList;